import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useActiveProject } from '../../../../context/ActiveProjectContext';
import { useMenuAccess } from '../../../../context/MenuAccessContext';
import { useAccessManifest } from '../../../../context/AccessManifestContext';
import { useCompanyProjects } from '../../../../context/CompanyProjectsContext';
import { useProgressiveNavContext } from '../../../../hooks/useProgressiveNavContext';
import { useProjectActivator } from '../../../../hooks/useCompanyPersonnelQueries';
import { useProgressiveMenuAccess } from '../progressiveNav';
import { getLatestLocations, getRoadTracks, getSPPointsForMap } from '../../../../services/fieldOperationsApi';
import { cachePersonnelData, cacheRoadsData, cacheSPData } from '../../../../utils/offlineStorage';
import { getMyPettyCashFunds } from '../../../finance/pettycash/pettyCashService';
import { useChatNotifications } from '../../../../context/ChatNotificationContext';
import useApprovedBudgets from '../../../budget/shared/hooks/useApprovedBudgets';
import { useProjectHasApprovedBudget } from '../../../../hooks/useProjectHasApprovedBudget';
import { useProjectBaseline } from '../../../../hooks/useBudgetQueries';
import { PATH_TO_LEVEL1_MENU, INVENTORY_FULL_CHILD_ROUTES, resolveMobileLevel1MenuId } from '../menuRoutingConfig';
import { PROJECT_CHILD_ROUTES } from '../projects/projectNavConfig';
import { isJmOperatorProfile } from '../../../../utils/accessProfiles';
import {
  buildMainMenus,
  buildSubSections,
  buildMenuItems,
  createFieldInventoryMetroTile,
} from './mobileMetroMenuData';

/**
 * State, access flags, offline sync, and navigation handlers for MobileMetroMenu.
 */
export default function useMobileMetroMenuController({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { activeProject, setActiveProject } = useActiveProject();
  const activateProject = useProjectActivator();
  const { approvedPresence, survey: approvedSurveyBudget, drilling: approvedDrillingBudget, recording: approvedRecordingBudget } = useApprovedBudgets(activeProject?.project_id);
  const hasApprovedBudget = useProjectHasApprovedBudget(activeProject?.project_id);
  const showHelicopterOpsMenu = activeProject?.has_helicopter === true;
  const {
    hasMenuAccess: baseHasMenuAccess,
    hasMenuAccessOrChild: baseHasMenuAccessOrChild,
    menusLoaded,
  } = useMenuAccess();
  const { isDemoProjectBootstrap } = useProgressiveNavContext();
  const {
    showCompanyMenu,
    hasMenuAccess,
    hasMenuAccessOrChild,
  } = useProgressiveMenuAccess({
    isDemoProjectBootstrap,
    hasMenuAccess: baseHasMenuAccess,
    hasMenuAccessOrChild: baseHasMenuAccessOrChild,
  });
  const { manifest } = useAccessManifest();
  const isPlatformDeveloper = manifest?.platformDeveloper === true;
  const hasFullInventoryAccess = hasMenuAccessOrChild('/inventory', INVENTORY_FULL_CHILD_ROUTES);
  const hasFieldInventoryAccess =
    hasMenuAccess('/inventory/mobile') ||
    hasMenuAccess('/inventory/new') ||
    hasMenuAccess('/inventory');
  const hasInventoryAccess = hasFullInventoryAccess || hasFieldInventoryAccess;
  const showFieldInventoryMetroCard =
    hasMenuAccess('/inventory/mobile') || hasMenuAccess('/inventory/new');
  const fieldInventoryDirectPath =
    showFieldInventoryMetroCard &&
    hasMenuAccess('/inventory/mobile') &&
    !hasMenuAccess('/inventory/new')
      ? '/inventory/mobile'
      : showFieldInventoryMetroCard &&
          !hasMenuAccess('/inventory/mobile') &&
          hasMenuAccess('/inventory/new')
        ? '/inventory/mobile/new'
        : undefined;
  const showGenericInventoryMetroTile =
    !showFieldInventoryMetroCard ||
    hasMenuAccess('/inventory/dashboard') ||
    hasMenuAccess('/inventory/kits') ||
    hasMenuAccess('/inventory/parameters') ||
    hasMenuAccess('/inventory/vendors') ||
    hasMenuAccess('/inventory');
  const { hasProjects, configuredProjects } = useCompanyProjects();
  const { hasUnreadOpsChat } = useChatNotifications();

  const [level, setLevel] = useState(1);
  const [level1Selection, setLevel1Selection] = useState(null);
  const [level2Selection, setLevel2Selection] = useState(null);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' && navigator.onLine);
  const [myOpenPettyFunds, setMyOpenPettyFunds] = useState([]);
  const [syncingOfflinePack, setSyncingOfflinePack] = useState(false);
  const [offlineSyncNote, setOfflineSyncNote] = useState({ type: '', message: '' });

  const isAdmin = auth.user && (auth.user.role === 'admin' || auth.user.role === 'superadmin');
  const isSuperAdmin = auth.user && auth.user.role === 'superadmin';
  const hasSurvey = activeProject?.has_survey === true;
  const hasDrilling = activeProject?.has_drilling === true;
  const hasRecording = activeProject?.has_recording_camp === true;
  const hasBaseCamp = activeProject?.has_base_camp === true;

  const { data: approvedManagementBaseline } = useProjectBaseline(
    activeProject?.project_id,
    'management',
    { enabled: !!activeProject?.project_id && hasBaseCamp },
  );

  const isProjectPersonnelOnly =
    !hasMenuAccessOrChild('/projects', PROJECT_CHILD_ROUTES) &&
    (hasMenuAccess('/operations/chat') || hasMenuAccess('/drilling/progress-map') || hasMenuAccess('/drilling/crew-report-field'));

  const isJmOperatorOnly = isJmOperatorProfile(manifest);

  const menuCtx = useMemo(() => ({
    isProjectPersonnelOnly,
    isJmOperatorOnly,
    hasMenuAccess,
    hasMenuAccessOrChild,
    showCompanyMenu,
    showFieldInventoryMetroCard,
    fieldInventoryMetroTile: (section) =>
      createFieldInventoryMetroTile(section, fieldInventoryDirectPath),
    hasInventoryAccess,
    showGenericInventoryMetroTile,
    hasProjects,
    activeProject,
    isAdmin,
    isDemoProjectBootstrap,
    isPlatformDeveloper,
    isSuperAdmin,
    hasSurvey,
    hasDrilling,
    hasRecording,
    hasBaseCamp,
    showHelicopterOpsMenu,
    hasApprovedBudget,
    approvedPresence,
    // Live tender grant, not a route permission — same gate as the desktop
    // sidebar so the tile cannot outlive it here.
    tenderAccess: manifest?.tender,
    surveyBudgetPath: approvedSurveyBudget?.budget_id
      ? `/budgets/survey/${approvedSurveyBudget.budget_id}`
      : '/budgets/survey/new',
    drillingBudgetPath: approvedDrillingBudget?.budget_id
      ? `/budgets/drilling/${approvedDrillingBudget.budget_id}`
      : '/budgets/drilling/new',
    recordingBudgetPath: approvedRecordingBudget?.budget_id
      ? `/budgets/recording/${approvedRecordingBudget.budget_id}`
      : '/budgets/recording/new',
    basecampBudgetPath: approvedManagementBaseline?.budget_id
      ? `/budgets/basecamp/${approvedManagementBaseline.budget_id}`
      : '/budgets/basecamp/new',
  }), [
    isProjectPersonnelOnly,
    isJmOperatorOnly,
    hasMenuAccess,
    hasMenuAccessOrChild,
    showCompanyMenu,
    showFieldInventoryMetroCard,
    fieldInventoryDirectPath,
    hasInventoryAccess,
    showGenericInventoryMetroTile,
    hasProjects,
    activeProject,
    isAdmin,
    isDemoProjectBootstrap,
    isPlatformDeveloper,
    isSuperAdmin,
    hasSurvey,
    hasDrilling,
    hasRecording,
    hasBaseCamp,
    showHelicopterOpsMenu,
    hasApprovedBudget,
    approvedPresence,
    approvedSurveyBudget,
    approvedDrillingBudget,
    approvedRecordingBudget,
    approvedManagementBaseline,
    manifest,
  ]);

  const getMainMenus = useCallback(() => buildMainMenus(menuCtx), [menuCtx]);
  const getSubSections = useCallback((menuId) => buildSubSections(menuId, menuCtx), [menuCtx]);
  const getMenuItems = useCallback(
    (menuId, sectionId) => buildMenuItems(menuId, sectionId, menuCtx),
    [menuCtx],
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const pathname = location.pathname;
      const match = PATH_TO_LEVEL1_MENU.find(
        ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
      if (match && match.menuId != null) {
        setLevel(2);
        setLevel1Selection(resolveMobileLevel1MenuId(match.menuId));
        setLevel2Selection(null);
      } else {
        setLevel(1);
        setLevel1Selection(null);
        setLevel2Selection(null);
      }
    }
  }, [isOpen, location.pathname]);

  useEffect(() => {
    if (!isOpen || !menusLoaded || !auth.user) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getMyPettyCashFunds({ status: 'OPEN' });
        if (!cancelled) {
          const openFunds = (Array.isArray(data?.items) ? data.items : []).filter(
            (fund) => String(fund?.status || '').toUpperCase() === 'OPEN',
          );
          setMyOpenPettyFunds(openFunds);
        }
      } catch {
        if (!cancelled) setMyOpenPettyFunds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, menusLoaded, auth.user]);

  const handleSyncOfflinePack = useCallback(async () => {
    if (syncingOfflinePack) return;
    if (!online) {
      setOfflineSyncNote({
        type: 'warning',
        message: 'Connect to the internet to refresh offline data.',
      });
      return;
    }
    if (!activeProject?.project_id) {
      setOfflineSyncNote({
        type: 'warning',
        message: 'Select an active project before syncing offline data.',
      });
      return;
    }

    setSyncingOfflinePack(true);
    setOfflineSyncNote({ type: 'info', message: 'Syncing Field Operations offline pack...' });
    try {
      const pid = activeProject.project_id;
      const [spData, personnelData, roadsData] = await Promise.all([
        getSPPointsForMap(pid),
        getLatestLocations(pid, 120),
        getRoadTracks(pid),
      ]);

      await Promise.all([
        cacheSPData(pid, spData),
        cachePersonnelData(pid, personnelData?.personnel || []),
        cacheRoadsData(pid, Array.isArray(roadsData) ? roadsData : []),
      ]);

      setOfflineSyncNote({
        type: 'success',
        message: 'Offline pack ready for 24 hours on this device.',
      });
    } catch (error) {
      console.error('Offline sync failed:', error);
      setOfflineSyncNote({
        type: 'danger',
        message: 'Could not sync offline pack. Try again with stable internet.',
      });
    } finally {
      setSyncingOfflinePack(false);
    }
  }, [activeProject?.project_id, online, syncingOfflinePack]);

  useEffect(() => {
    if (!offlineSyncNote?.message) return undefined;
    const timer = setTimeout(() => {
      setOfflineSyncNote({ type: '', message: '' });
    }, 6000);
    return () => clearTimeout(timer);
  }, [offlineSyncNote]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
    handleClose();
  }, [navigate, handleClose]);

  const handleLevel1Click = useCallback((menuId) => {
    if (menuId === 'projects' && hasProjects && !activeProject && !hasMenuAccess('/projects')) {
      handleNavigate('/projects/portfolio');
      return;
    }
    setLevel1Selection(menuId);
    setLevel(2);
  }, [hasProjects, activeProject, hasMenuAccess, handleNavigate]);

  const handleLevel2Click = useCallback((sectionId) => {
    if (level1Selection === 'inventory-field-entry') {
      if (sectionId === 'field-list') {
        handleNavigate('/inventory/mobile');
        return;
      }
      if (sectionId === 'field-add') {
        handleNavigate('/inventory/mobile/new');
        return;
      }
    }
    if (level1Selection === 'projects' && sectionId === 'list') {
      handleNavigate('/projects/portfolio');
      return;
    }
    setLevel2Selection(sectionId);
    setLevel(3);
  }, [level1Selection, handleNavigate]);

  const handleBack = useCallback(() => {
    if (level === 3) {
      setLevel(2);
      setLevel2Selection(null);
    } else if (level === 2) {
      setLevel(1);
      setLevel1Selection(null);
    }
  }, [level]);

  const handleQuickActivateProject = useCallback(async (project) => {
    if (activeProject?.project_id === project.project_id) {
      setShowProjectPicker(false);
      return;
    }
    try {
      const data = await activateProject(project.project_id);
      setActiveProject(data);
      setShowProjectPicker(false);
    } catch (err) {
      console.error('Error activating project:', err);
    }
  }, [activeProject, setActiveProject, activateProject]);

  const handleMenuItemClick = useCallback((item) => {
    if (item.path === '/operations/chat' && !online) return;
    if (item.path) {
      handleNavigate(item.path);
    } else if (item.action) {
      handleClose();
    }
  }, [online, handleNavigate, handleClose]);

  return {
    level,
    setLevel,
    level1Selection,
    level2Selection,
    showProjectPicker,
    setShowProjectPicker,
    online,
    myOpenPettyFunds,
    syncingOfflinePack,
    offlineSyncNote,
    activeProject,
    menusLoaded,
    hasMenuAccess,
    hasUnreadOpsChat,
    hasProjects,
    configuredProjects,
    isDemoProjectBootstrap,
    getMainMenus,
    getSubSections,
    getMenuItems,
    handleClose,
    handleNavigate,
    handleLevel1Click,
    handleLevel2Click,
    handleBack,
    handleQuickActivateProject,
    handleMenuItemClick,
    handleSyncOfflinePack,
  };
}
